import axios from 'axios';
import { DeviceDto, OdometerHistoryDto } from '@/features/devices/device.dto';
import { TrackingHistoryDto, TrackingHistoryEventDto } from '@/features/map/map.type';
import { GetThermometersParams, ThermometerDto, ThermometerUpdateDto } from '@/features/sensors/dtos/thermometer.dto';
import { SpeedLimitService } from '@/OptimusRoutes/constants';
import { ServiceResponse } from '../../OptimusRoutes/interfaces';
import { UpdateModelProfileDto, UpdateModelProfileResponseDto } from '../modelProfile/modelProfile.dto';
import { MultimediaDto, MultimediaEventDto } from '../multimedia/multimedia.dto';
import {
	ChangeEventStatusParams,
	CreateOdometerHistory,
	GetTrailHistoryEventsParams,
	GetTrailHistoryParams,
	UploadDeviceFile,
	UploadDevicesResponse,
} from './device.types';

type Service = {
	get: (id: number) => Promise<ServiceResponse<DeviceDto>>;
	getMultimediaVideo: (id: number, startDate: string, endDate: string) => Promise<ServiceResponse<MultimediaDto[]>>;
	getEventsMultimedia: (
		id: number,
		startDate: string,
		endDate: string
	) => Promise<ServiceResponse<MultimediaEventDto[]>>;
	getEventsMultimediaByTrackingHistoryId: (
		id: number,
		trackingHistoryId: string
	) => Promise<ServiceResponse<MultimediaEventDto[]>>;
	getTrailHistory: (args: GetTrailHistoryParams) => Promise<ServiceResponse<TrackingHistoryDto[]>>;
	getTrailHistoryEvents: (args: GetTrailHistoryEventsParams) => Promise<ServiceResponse<TrackingHistoryEventDto[]>>;
	updateUnitDetails: (id: number, description: string, timeZoneId: number) => Promise<ServiceResponse>;
	uploadFile: (data: UploadDeviceFile) => Promise<ServiceResponse<UploadDevicesResponse>>;
	getThermometers: (params: GetThermometersParams) => Promise<ServiceResponse<ThermometerDto[]>>;
	createThermometer: (id: number, order: number) => Promise<ServiceResponse<ThermometerDto>>;
	updateThermometer: (
		id: number,
		thermometerId: number,
		update: ThermometerUpdateDto
	) => Promise<ServiceResponse<ThermometerDto>>;
	changePostedSpeedLimitStatus: (deviceId: number, speedLimitService: SpeedLimitService) => Promise<ServiceResponse>;
	deleteDeviceTrackingHistory: (id: number) => Promise<ServiceResponse>;
	deletePositionById: (deviceId: number, trackingId: string) => Promise<ServiceResponse>;
	changeEventStatus: (eventData: ChangeEventStatusParams) => Promise<ServiceResponse>;
	createOdometerHistory: (odometerHistory: CreateOdometerHistory) => Promise<ServiceResponse<OdometerHistoryDto>>;
	updateDevicesModelProfile: (body: UpdateModelProfileDto) => Promise<ServiceResponse<UpdateModelProfileResponseDto>>;
	shareDevicesWithClient: (deviceIdList: number[], clientIdList: number[]) => Promise<ServiceResponse>;
	stopSharingDeviceWithClients: (deviceId: number, clientIdList: number[]) => Promise<ServiceResponse>;
};

const get = async (id: number): Promise<ServiceResponse<DeviceDto>> => {
	const { data } = await axios.get<ServiceResponse<DeviceDto>>(`/v2/devices/${id}`);
	return data;
};

const updateDevicesModelProfile = async (
	body: UpdateModelProfileDto
): Promise<ServiceResponse<UpdateModelProfileResponseDto>> => {
	const { data } = await axios.patch<ServiceResponse<UpdateModelProfileResponseDto>>(
		'/v2/devices/model-profiles',
		body
	);
	return data;
};
const getEventsMultimedia = async (
	id: number,
	startDate: string,
	endDate: string
): Promise<ServiceResponse<MultimediaEventDto[]>> => {
	let options = {
		params: {
			'start-date': startDate,
			'end-date': endDate,
		},
	};

	const { data } = await axios.get<ServiceResponse<MultimediaEventDto[]>>(
		`/v2/devices/${id}/multimedia-events`,
		options
	);
	return data;
};

const getMultimediaVideo = async (
	id: number,
	startDate: string,
	endDate: string
): Promise<ServiceResponse<MultimediaDto[]>> => {
	let options = {
		params: {
			'start-date': startDate,
			'end-date': endDate,
		},
	};

	const { data } = await axios.get<ServiceResponse<MultimediaDto[]>>(`/v2/devices/${id}/multimedia-videos`, options);
	return data;
};

const getTrailHistory = async ({
	id,
	startDate,
	endDate,
	cancelToken,
}: GetTrailHistoryParams): Promise<ServiceResponse<TrackingHistoryDto[]>> => {
	const params = {
		'start-date': startDate,
		'end-date': endDate,
	};

	const { data } = await axios.get<ServiceResponse<TrackingHistoryDto[]>>(`/v2/devices/${id}/trail-history`, {
		params,
		cancelToken,
	});
	return data;
};

const getTrailHistoryEvents = async ({
	id,
	startDate,
	endDate,
	cancelToken,
}: GetTrailHistoryEventsParams): Promise<ServiceResponse<TrackingHistoryEventDto[]>> => {
	const params = {
		'start-date': startDate,
		'end-date': endDate,
	};

	const { data } = await axios.get<ServiceResponse<TrackingHistoryEventDto[]>>(
		`/v2/devices/${id}/trail-history/events`,
		{
			params,
			cancelToken,
		}
	);
	return data;
};

const getEventsMultimediaByTrackingHistoryId = async (
	id: number,
	trackingHistoryId: string
): Promise<ServiceResponse<MultimediaEventDto[]>> => {
	const { data } = await axios.get<ServiceResponse<MultimediaEventDto[]>>(
		`/v2/devices/${id}/multimedia-events/${trackingHistoryId}`
	);
	return data;
};

const uploadFile = async (values: UploadDeviceFile): Promise<ServiceResponse<UploadDevicesResponse>> => {
	const formData = new FormData();
	(Object.keys(values) as (keyof UploadDeviceFile)[]).forEach((key) => {
		const value = values[key];
		if (key === 'file') {
			formData.append('file', values.file);
			return;
		}
		if (value !== undefined && value !== null) {
			formData.append(key, value.toString());
		}
	});
	const { data } = await axios.post<ServiceResponse<UploadDevicesResponse>>('/v2/devices/upload-file', formData, {
		headers: { 'Content-Type': 'multipart/form-data' },
	});
	return data;
};

const updateUnitDetails = async (id: number, description: string, timeZoneId: number): Promise<ServiceResponse> => {
	const { data } = await axios.patch<ServiceResponse>(`/v2/devices/${id}/details`, {
		description,
		timeZoneId,
	});
	return data;
};

const changePostedSpeedLimitStatus = async (
	deviceId: number,
	speedLimitService: SpeedLimitService
): Promise<ServiceResponse> => {
	const { data } = await axios.post<ServiceResponse>(`/v2/devices/${deviceId}/posted-speed-limit`, {
		speedLimitService,
	});
	return data;
};

const getThermometers = async ({
	id,
	includeAlertConfig,
	status,
}: GetThermometersParams): Promise<ServiceResponse<ThermometerDto[]>> => {
	const { data } = await axios.get<ServiceResponse<ThermometerDto[]>>(`/v2/devices/${id}/thermometers`, {
		params: { includeAlertConfig, status },
	});
	return data;
};

const createThermometer = async (id: number, order: number): Promise<ServiceResponse<ThermometerDto>> => {
	const { data } = await axios.post<ServiceResponse<ThermometerDto>>(`/v2/devices/${id}/thermometers`, { order });
	return data;
};
const updateThermometer = async (
	id: number,
	thermometerId: number,
	update: ThermometerUpdateDto
): Promise<ServiceResponse<ThermometerDto>> => {
	const { data } = await axios.patch<ServiceResponse<ThermometerDto>>(
		`/v2/devices/${id}/thermometers/${thermometerId}`,
		update
	);
	return data;
};

const deleteDeviceTrackingHistory = async (id: number): Promise<ServiceResponse> => {
	const { data } = await axios.delete<ServiceResponse>(`/v2/devices/${id}/tracking-history`);
	return data;
};

const deletePositionById = async (deviceId: number, trackingId: string): Promise<ServiceResponse> => {
	const { data } = await axios.delete<ServiceResponse>(`/v2/devices/${deviceId}/positions/${trackingId}`);
	return data;
};

const changeEventStatus = async (eventData: ChangeEventStatusParams): Promise<ServiceResponse> => {
	const { data } = await axios.post<ServiceResponse>('/v2/devices/event-status', eventData);
	return data;
};

const createOdometerHistory = async (
	odometerHistory: CreateOdometerHistory
): Promise<ServiceResponse<OdometerHistoryDto>> => {
	const { data } = await axios.post<ServiceResponse<OdometerHistoryDto>>(
		'/v2/devices/odometer-history',
		odometerHistory
	);
	return data;
};

const shareDevicesWithClient = async (deviceIdList: number[], clientIdList: number[]): Promise<ServiceResponse> => {
	const { data } = await axios.post<ServiceResponse>('/v2/devices/share-devices-with-client', {
		deviceIdList,
		clientIdList,
	});
	return data;
};

const stopSharingDeviceWithClients = async (deviceId: number, clientIdList: number[]): Promise<ServiceResponse> => {
	const { data } = await axios.delete<ServiceResponse>('/v2/devices/stop-sharing-devices-with-client', {
		data: { deviceId, clientIdList },
	});
	return data;
};

const DeviceService: Service = {
	get,
	updateDevicesModelProfile,
	getMultimediaVideo,
	getEventsMultimedia,
	getEventsMultimediaByTrackingHistoryId,
	uploadFile,
	changePostedSpeedLimitStatus,
	getThermometers,
	createThermometer,
	updateThermometer,
	deleteDeviceTrackingHistory,
	deletePositionById,
	getTrailHistory,
	getTrailHistoryEvents,
	changeEventStatus,
	createOdometerHistory,
	updateUnitDetails,
	shareDevicesWithClient,
	stopSharingDeviceWithClients,
};

export default DeviceService;
